/**
 * Formatting utilities for the Token Table
 */

/**
 * Format a number as USD currency with abbreviations
 * Examples: $1.23M, $456.78K, $123.45
 */
export function formatUSD(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '--';

  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(2)}K`;
  }
  if (value < 1) {
    return `$${value.toFixed(2)}`;
  }
  return `$${value.toFixed(2)}`;
}

/**
 * Format a percentage with sign and color indication
 * Examples: +15.64%, -3.42%
 */
export function formatPercent(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '--';

  if (Math.abs(value) > 999.99) {
    return value >= 0 ? '>999%' : '<-999%';
  }

  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

/**
 * Determine if a percentage is positive (for styling)
 */
export function isPositiveChange(value: number | null | undefined): boolean {
  return value != null && !isNaN(value) && value >= 0;
}

/**
 * Format holder count with abbreviations
 * Examples: 2.46K, 714
 */
export function formatHolders(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '--';

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(2)}K`;
  }
  return value.toString();
}

/**
 * Format price with dynamic decimals for small values
 * Examples: $0.00086, $0.000042, $1.23
 */
export function formatPrice(price: number | null | undefined): string {
  if (price == null || isNaN(price)) return '--';

  if (price === 0) return '$0.00';

  // Very small values - use fixed notation (no scientific notation)
  if (price < 0.00001) {
    return `$${price.toFixed(8)}`;
  }

  // Small values - show more decimals
  if (price < 0.001) {
    return `$${price.toFixed(8)}`;
  }

  if (price < 0.01) {
    return `$${price.toFixed(6)}`;
  }

  if (price < 1) {
    return `$${price.toFixed(4)}`;
  }

  return `$${price.toFixed(2)}`;
}

/**
 * Format a number with K/M abbreviations (no currency symbol)
 */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '--';

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toString();
}

/**
 * Format a number with K/M/B abbreviations and optional prefix/suffix
 * Examples: $1.2M, 456K, $123.45
 */
export function formatCompactNumber(
  value: number | null | undefined,
  options?: { prefix?: string; suffix?: string; decimals?: number }
): string {
  if (value == null || isNaN(value)) return '--';

  const prefix = options?.prefix ?? '';
  const suffix = options?.suffix ?? '';
  const decimals = options?.decimals ?? 2;

  if (value >= 1_000_000_000) {
    return `${prefix}${(value / 1_000_000_000).toFixed(decimals)}B${suffix}`;
  }
  if (value >= 1_000_000) {
    return `${prefix}${(value / 1_000_000).toFixed(decimals)}M${suffix}`;
  }
  if (value >= 1_000) {
    return `${prefix}${(value / 1_000).toFixed(decimals)}K${suffix}`;
  }
  return `${prefix}${value.toFixed(decimals)}${suffix}`;
}

/**
 * Format token amount with max 4 decimals and K/M/B notation for large values
 * Examples: 1.2345, 12.34K, 1.23M, 4.56B
 */
export function formatCompactTokenAmount(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '--';
  if (value === 0) return '0';

  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(2)}K`;

  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/**
 * Format SOL amount with max 6 decimals and adaptive precision
 * Examples: 0.000001, 0.1234, 1.234567
 */
export function formatCompactSolAmount(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '--';
  if (value === 0) return '0';

  if (value < 0.001) return value.toFixed(6);
  if (value < 1) return value.toFixed(4);

  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/**
 * Truncate an address for display
 */
export function truncateAddress(address: string, chars = 4): string {
  if (!address) return '';
  if (address.length <= chars * 2 + 3) return address;
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}

/**
 * Format a date as relative time ago
 */
export function formatTimeAgo(date: Date | string): string {
  const now = new Date();
  const then = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}
